import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Alert, Button, Spinner, Table, ButtonGroup, Modal, InputGroup, SortableTh, PageHeader } from '@/components/ui';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../../hooks/usePermissions';
import { useDeviceSize } from '../../../hooks/useDeviceSize';
import Toast, { ToastType } from '../../../components/Toast';
import standingTaskService, { StandingTask, StandingTaskFilters, StandingTaskFilterOptions } from '../../../services/standingTaskService';
import TaskTypeSelectionModal from '../components/modals/TaskTypeSelectionModal';
import OverheadStandingTaskModal from '../components/modals/OverheadStandingTaskModal';
import RoutineTaskModal from '../components/modals/RoutineTaskModal';
import QuantifiedTaskModal from '../components/modals/QuantifiedTaskModal';
import OverheadTaskModal from '../../overhead/components/modals/OverheadTaskModal';
import { RoutineTask, TaskSubType } from '../../../types/RoutineTask';
import { Activity } from '../../../types/Activity';
import { activityService } from '../../../services/activityService';
import { SearchableDropdown } from '../../../components/SearchableDropdown';
import { MultiSelectDropdown } from '../../../components/MultiSelectDropdown';
import FiltersCard from '../../../components/FiltersCard';
import ServerPagination from '../../../components/ServerPagination';
import { formatStatusForDisplay } from '../../../utils/statusUtils';

const StandingTasksPage: React.FC = () => {
  const [tasks, setTasks] = useState<StandingTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showTypeSelection, setShowTypeSelection] = useState(false);
  const [showRoutineModal, setShowRoutineModal] = useState(false);
  const [showQuantifiedModal, setShowQuantifiedModal] = useState(false);
  const [showOverheadModal, setShowOverheadModal] = useState(false);
  const [editingTask, setEditingTask] = useState<StandingTask | null>(null);
  const [toast, setToast] = useState<{ show: boolean; message: string; type: ToastType }>({
    show: false,
    message: '',
    type: 'info'
  });
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const { hasPermission } = usePermissions();
  const { isMobile } = useDeviceSize();
  const navigate = useNavigate();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<number | null>(null);
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    totalCount: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false
  });

  // Filters state (applied to server)
  const [filters, setFilters] = useState<StandingTaskFilters>({
    type: 'All',
    status: [],
    serviceArea: '',
    activityName: [],
    search: ''
  });

  // Pending filters (not yet applied)
  const [pendingFilters, setPendingFilters] = useState<StandingTaskFilters>({
    type: 'All',
    status: [],
    serviceArea: '',
    activityName: [],
    search: ''
  });

  // Filter options from server
  const [filterOptions, setFilterOptions] = useState<StandingTaskFilterOptions>({
    serviceAreas: [],
    activities: [],
    statuses: [],
    types: ['All', 'Routine', 'Quantified', 'Overhead']
  });

  // Fetch filter options on mount
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const options = await standingTaskService.getFilterOptions();
        setFilterOptions({
          ...options,
          types: ['All', 'Routine', 'Quantified', 'Overhead']
        });
      } catch (error) {
        console.error('Error fetching filter options:', error);
      }
    };

    fetchFilterOptions();
  }, []);

  // Fetch tasks when pagination or filters change
  useEffect(() => {
    fetchTasks();
  }, [pagination.page, pagination.limit, filters, searchTerm, sortBy, sortDir]);

  const handleSort = (key: string, dir: 'asc' | 'desc') => {
    setSortBy(key);
    setSortDir(dir);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const fetchTasks = async () => {
    try {
      setLoading(true);
      
      // Combine filters with search term
      const combinedFilters: StandingTaskFilters = {
        ...filters,
        search: searchTerm,
        ...(sortBy ? { sortBy, sortOrder: sortDir } : {})
      };

      const response = await standingTaskService.getStandingTasksPaginated(
        pagination.page,
        pagination.limit,
        combinedFilters
      );

      setTasks(response.data);
      setPagination(prev => ({
        ...prev,
        totalCount: response.pagination.totalCount,
        totalPages: response.pagination.totalPages,
        hasNext: response.pagination.hasNext,
        hasPrevious: response.pagination.hasPrevious
      }));
      setError(null);
    } catch (err) {
      console.error('Error fetching tasks:', err);
      setError('Failed to fetch tasks');
      setTasks([]);
      setToast({
        show: true,
        message: 'Failed to fetch tasks',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTask = () => {
    setEditingTask(null);
    setShowTypeSelection(true);
  };

  const handleTaskTypeSelect = (type: 'Routine' | 'Quantified' | 'Overhead') => {
    setShowTypeSelection(false);
    if (type === 'Routine') {
      setShowRoutineModal(true);
    } else if (type === 'Quantified') {
      setShowQuantifiedModal(true);
    } else if (type === 'Overhead') {
      setShowOverheadModal(true);
    }
  };

  const handleEditTask = (taskId: number) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    setEditingTask(task);
    if (task.type === 'Routine') {
      setShowRoutineModal(true);
    } else if (task.type === 'Quantified') {
      setShowQuantifiedModal(true);
    } else if (task.type === 'Overhead') {
      setShowOverheadModal(true);
    }
  };

  const handleTaskSaved = async () => {
    setShowRoutineModal(false);
    setShowQuantifiedModal(false);
    setShowOverheadModal(false);
    setEditingTask(null);
    setToast({
      show: true,
      message: editingTask ? 'Task updated successfully' : 'Task created successfully',
      type: 'success'
    });
    // Refresh the task list
    await fetchTasks();
  };

  const handleFilterChange = (name: string, value: string | string[]) => {
    setPendingFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleApplyFilters = () => {
    setFilters(pendingFilters);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to page 1 when applying filters
  };

  const handleClearFilters = () => {
    const clearedFilters: StandingTaskFilters = {
      type: 'All',
      status: [],
      serviceArea: '',
      activityName: [],
      search: ''
    };
    setPendingFilters(clearedFilters);
    setFilters(clearedFilters);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const hasActiveFilters = () => {
    return (filters.type && filters.type !== 'All') ||
           (filters.status && filters.status.length > 0) ||
           (filters.serviceArea && filters.serviceArea !== '') ||
           (filters.activityName && filters.activityName.length > 0);
  };

  const hasUnappliedChanges = () => {
    const compareArrays = (a: string[] | undefined, b: string[] | undefined): boolean => {
      const arrA = a || [];
      const arrB = b || [];
      return arrA.length !== arrB.length || !arrA.every((v, i) => v === arrB[i]);
    };
    
    return pendingFilters.type !== filters.type ||
           compareArrays(pendingFilters.status, filters.status) ||
           pendingFilters.serviceArea !== filters.serviceArea ||
           compareArrays(pendingFilters.activityName, filters.activityName);
  };

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const handlePageSizeChange = (limit: number) => {
    setPagination(prev => ({ ...prev, limit, page: 1 }));
  };

  const handleModalClose = () => {
    setShowRoutineModal(false);
    setShowQuantifiedModal(false);
    setShowOverheadModal(false);
    setEditingTask(null);
  };

  const handleDeleteClick = (taskId: number) => {
    setTaskToDelete(taskId);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (taskToDelete) {
      await handleDeleteTask(taskToDelete);
      setShowDeleteConfirm(false);
      setTaskToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setTaskToDelete(null);
  };

  const handleDeleteTask = async (taskId: number) => {
    try {
      await standingTaskService.deleteStandingTask(taskId);
      setToast({
        show: true,
        message: 'Task deleted successfully',
        type: 'success'
      });
      // Refresh the task list
      await fetchTasks();
    } catch (err) {
      console.error('Error deleting task:', err);
      setToast({
        show: true,
        message: 'Failed to delete task',
        type: 'error'
      });
    }
  };

  const toggleSearch = () => {
    setIsSearchVisible(!isSearchVisible);
    if (isSearchVisible) {
      setSearchInput('');
      setSearchTerm('');
    }
  };

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSearchInputChange = (value: string) => {
    setSearchInput(value);
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
  };

  const mapToRoutineTask = (task: StandingTask): RoutineTask | null => {
    if (task.type !== 'Routine') return null;
    
    return {
      id: task.id,
      taskId: task.id,
      type: 'Routine',
      description: task.description || '',
      status: task.status,
      taskSubType: (task.routineTasks?.[0]?.taskSubType as TaskSubType) || TaskSubType.Numbered_NonNumbered,
      activities: task.activities as Activity[] || [],
      labourAllocation: task.routineTasks?.[0]?.labourAllocation || 100,
      materialAllocation: task.routineTasks?.[0]?.materialAllocation || 100,
      equipmentAllocation: task.routineTasks?.[0]?.equipmentAllocation || 100,
      secondaryLabourAllocation: task.routineTasks?.[0]?.secondaryLabourAllocation || 0,
      secondaryMaterialAllocation: task.routineTasks?.[0]?.secondaryMaterialAllocation || 0,
      secondaryEquipmentAllocation: task.routineTasks?.[0]?.secondaryEquipmentAllocation || 0,
      name: task.name,
      mode: task.mode,
      taskNumber: task.taskNumber,
      createdAt: task.createdAt
    };
  };

  const mapToQuantifiedTask = (task: StandingTask) => {
    if (task.type !== 'Quantified') return null;
    
    // Get the activity ID from the activities array
    // Only set activityId if there's actually an activity
    const activityId = task.activities && task.activities.length > 0 
      ? task.activities[0].id 
      : undefined;
    
    const result: {
      id: number;
      taskNumber: string;
      name: string;
      activityId?: number;
      description: string;
    } = {
      id: task.id,
      taskNumber: task.taskNumber,
      name: task.name,
      description: task.description || ''
    };
    
    // Only include activityId if it exists
    if (activityId !== undefined) {
      result.activityId = activityId;
    }
    
    return result;
  };

  const mapToOverheadTask = (task: StandingTask): RoutineTask | null => {
    if (task.type !== 'Overhead') return null;
    
    return {
      id: task.id,
      taskId: task.id,
      type: 'Routine',
      description: task.description || '',
      status: task.status,
      taskSubType: TaskSubType.Numbered_NonNumbered,
      activities: task.activities as Activity[] || [],
      labourAllocation: 100,
      materialAllocation: 100,
      equipmentAllocation: 100,
      secondaryLabourAllocation: 0,
      secondaryMaterialAllocation: 0,
      secondaryEquipmentAllocation: 0,
      name: task.name,
      mode: task.mode || 'standing',
      taskNumber: task.taskNumber,
      createdAt: task.createdAt,
      foremanAreaId: task.foremanArea?.id
    };
  };

  if (!hasPermission('standing:tasks_view')) {
    return (
      <Container>
        <Alert variant="danger">You do not have permission to view this page.</Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="tw-p-4">
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast(prev => ({ ...prev, show: false }))}
      />
      
      <PageHeader
        title="Standing Tasks"
        actions={[
          {
            key: 'refresh',
            label: 'Refresh',
            // Was `variant="success"` (green) — Refresh is chrome, not a success outcome.
            icon: 'bi-arrow-clockwise',
            intent: 'neutral',
            onClick: () => {
              setLoading(true);
              Promise.all([
                fetchTasks(),
                standingTaskService.getFilterOptions().then(options =>
                  setFilterOptions({
                    ...options,
                    types: ['All', 'Routine', 'Quantified', 'Overhead']
                  })
                )
              ]).finally(() => setLoading(false));
            },
          },
          {
            key: 'search',
            label: `Search${searchTerm ? ` (${searchTerm.length > 20 ? searchTerm.substring(0, 20) + '…' : searchTerm})` : ''}`,
            icon: isSearchVisible ? 'bi-x-lg' : 'bi-search',
            intent: 'secondary',
            active: isSearchVisible || !!searchTerm,
            title: isSearchVisible ? 'Close search' : 'Search tasks',
            onClick: toggleSearch,
          },
          {
            key: 'create',
            label: 'Create Task',
            icon: 'bi-plus-lg',
            intent: 'primary',
            hidden: !hasPermission('standing:tasks_create'),
            onClick: handleCreateTask,
          },
        ]}
      />

      {error && (
        <Alert variant="danger" className="tw-mb-4">
          {error}
        </Alert>
      )}

      {/* Filters Card */}
      <FiltersCard
        title="Standing Task Filters"
        subtitle="Filter standing tasks by type, service area, activity, and status"
        collapsible={true}
        defaultCollapsed={false}
        showClearButton={true}
        onClear={handleClearFilters}
        hasActiveFilters={hasActiveFilters()}
        showApplyButton={true}
        onApply={handleApplyFilters}
        applyDisabled={!hasUnappliedChanges()}
      >
        <Row className="tw-items-end">
          <Col md={2}>
            <SearchableDropdown
              options={filterOptions.types}
              value={pendingFilters.type || 'All'}
              onChange={(value) => handleFilterChange('type', value)}
              getValue={(type) => type}
              formatOption={(type) => type}
              placeholder="All Types"
              className="tw-w-full"
              label="Task Type"
              noOptionsMessage="No types available"
            />
          </Col>
          <Col md={3}>
            <SearchableDropdown
              options={filterOptions.serviceAreas}
              value={pendingFilters.serviceArea || ''}
              onChange={(value) => handleFilterChange('serviceArea', value)}
              getValue={(area) => area.name}
              formatOption={(area) => area.name}
              placeholder="All Service Areas"
              className="tw-w-full"
              label="Service Area"
              noOptionsMessage="No service areas available"
            />
          </Col>
          <Col md={3}>
            <MultiSelectDropdown
              options={filterOptions.activities}
              value={pendingFilters.activityName || []}
              onChange={(value) => handleFilterChange('activityName', value.map(v => String(v)))}
              getValue={(activity) => activity.activityName}
              formatOption={(activity) => `#${activity.activityNumber} - ${activity.activityName}`}
              placeholder="All Activities"
              className="tw-w-full"
              label="Activity"
              noOptionsMessage="No activities available"
            />
          </Col>
          <Col md={2}>
            <MultiSelectDropdown<string>
              options={filterOptions.statuses}
              value={pendingFilters.status || []}
              onChange={(value) => handleFilterChange('status', value.map(v => String(v)))}
              getValue={(status) => status}
              formatOption={(status) => formatStatusForDisplay(status)}
              placeholder="All Statuses"
              className="tw-w-full"
              label="Status"
              noOptionsMessage="No statuses available"
            />
          </Col>
        </Row>
      </FiltersCard>

      {/* Search field */}
      {isSearchVisible && (
        <Row className="tw-mb-3">
          <Col xs={12}>
            <InputGroup className="tw-shadow-sm">
              <InputGroup.Text className="tw-bg-primary tw-text-white tw-border-primary">
                <i className="bi bi-search"></i>
              </InputGroup.Text>
              <Form.Control
                type="text"
                className="tw-border-primary/40"
                placeholder="Search by Task Name, Task Number, Description, Activity, or Service Area..."
                value={searchInput}
                onChange={(e) => handleSearchInputChange(e.target.value)}
                onKeyDown={handleSearchKeyPress}
                autoFocus
              />
              <Button
                variant="primary"
                type="button"
                onClick={handleSearch}
                title="Search"
              >
                <i className="bi bi-search"></i>
              </Button>
              {(searchInput || searchTerm) && (
                <Button
                  variant="outline-primary"
                  type="button"
                  onClick={clearSearch}
                  title="Clear search"
                >
                  <i className="bi bi-x-lg"></i>
                </Button>
              )}
            </InputGroup>
            {searchTerm && (
              <div className="tw-text-content-muted tw-text-sm tw-mt-1">
                Searching for: "{searchTerm}"
              </div>
            )}
          </Col>
        </Row>
      )}

      {loading ? (
        <div className="tw-text-center tw-py-4">
          <Spinner animation="border" className="tw-text-primary">Loading...</Spinner>
        </div>
      ) : (
        <>
          <div>
            <Table hover responsive>
              <thead>
                <tr>
                  <SortableTh label="Task Number" sortKey="taskNumber" activeKey={sortBy} direction={sortDir} onSort={handleSort} />
                  <SortableTh label="Name" sortKey="name" activeKey={sortBy} direction={sortDir} onSort={handleSort} />
                  <SortableTh label="Type" sortKey="type" activeKey={sortBy} direction={sortDir} onSort={handleSort} />
                  <SortableTh label="Status" sortKey="status" activeKey={sortBy} direction={sortDir} onSort={handleSort} />
                  <th>Service Area</th>
                  <SortableTh label="Created By" sortKey="createdBy" activeKey={sortBy} direction={sortDir} onSort={handleSort} />
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((task) => (
                  <tr key={task.id}>
                    <td>{task.taskNumber}</td>
                    <td>{task.name}</td>
                    <td>{task.type}</td>
                    <td>{formatStatusForDisplay(task.status)}</td>
                    <td>{task.foremanArea?.serviceArea?.name || (task as any).project?.serviceArea?.name || 'N/A'}</td>
                    <td>{task.createdBy ? `${task.createdBy.name} ${task.createdBy.lastName}` : 'N/A'}</td>
                    <td>
                      <ButtonGroup>
                        {hasPermission('standing:tasks_edit') && (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="tw-mr-2"
                            onClick={() => handleEditTask(task.id)}
                          >
                            Edit
                          </Button>
                        )}
                        {hasPermission('standing:tasks_delete') && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteClick(task.id)}
                          >
                            Delete
                          </Button>
                        )}
                      </ButtonGroup>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {/* Server-side pagination */}
          <ServerPagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            totalCount={pagination.totalCount}
            pageSize={pagination.limit}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            loading={loading}
            className="tw-mt-3 tw-mb-4"
          />
        </>
      )}

      <TaskTypeSelectionModal
        show={showTypeSelection}
        onClose={() => setShowTypeSelection(false)}
        onSelect={handleTaskTypeSelect}
      />

      <RoutineTaskModal
        open={showRoutineModal}
        task={editingTask ? mapToRoutineTask(editingTask) : null}
        onCancel={handleModalClose}
        onSuccess={handleTaskSaved}
      />

      <QuantifiedTaskModal
        open={showQuantifiedModal}
        task={editingTask ? mapToQuantifiedTask(editingTask) : null}
        onCancel={handleModalClose}
        onSuccess={handleTaskSaved}
      />

      <OverheadTaskModal
        open={showOverheadModal}
        task={editingTask ? mapToOverheadTask(editingTask) : null}
        onCancel={handleModalClose}
        onSuccess={handleTaskSaved}
      />

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteConfirm} onHide={handleDeleteCancel}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this task? This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleDeleteCancel}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default StandingTasksPage; 